import React, { FC, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCountCheckedProducts, selectIsRequiredAddressField, selectSuggestedOrganization } from '~/src/redux/lead/lead.selectors';
import { selectReferralCode } from '~/src/redux/referral-lead/referral-lead.selectors';
import { selectFilesData } from '~/src/redux/documents/documents.selectors';
import { selectPopupsStack } from '~/src/redux/popups/popups.selectors';
import { setClearCountCheckedProducts } from '~/src/redux/lead/lead.slice';
import { setIsShowGeolocationBlock } from '~/src/redux/geolocation/geolocation.slice';
import { setReferralCode } from '~/src/redux/referral-lead/referral-lead.slice';
import BannerLogo from '~/src/images/referral-product/banner-logo.png';
import { ProductsBlock } from '~/src/components/blocks/referral-products/products/products-block';
import { OrganizationInfoRef } from '~/src/components/blocks/referral-products/organization-info-ref/organization-info';
import { AdditionallyBlock } from '~/src/components/blocks/referral-products/additionally-block/additionally-block';
import { ChipsNotification } from '~/src/components/common-components/chips-notification/chips-notification';
import PopupContent from '~/src/module-federation/popup-content/popup-content';
import Popup from '~/src/components/common-components/popup/popup';
import { ReferralProductHeader } from '~/src/components/mini-sections/referral-product-header/referral-product-header';
import { ReferralProductFooter } from '~/src/components/mini-sections/referral-product-footer/referral-product-footer';
import { InfoBadge } from '~/src/components/common-components/info-badge/info-badge';
import { SvgShapeIcon } from '~/src/components/svg-components/shape-icon';
import { useFetchCreateLeadAllReferral } from '~/src/repositories/lead/hooks/use-fetch-create-lead-all-referral.hook';
import { useClientRequestForm } from '~/src/context/client-request-form/use-client-request-form.hook';
import { useBadgeHook } from '~/src/hooks/use-badge.hook';
import { useLeadFormValidation } from '~/src/hooks/use-form-lead-validation';
import { useFormReset } from '~/src/hooks/use-form-reset.hook';
import { useScroll } from '~/src/hooks/useScroll';
import { usePopupContent } from '~/src/components/common-components/popup-content/use-popup-content.hook';
import { determineProductAvailability, setClientType } from '~/src/lib/cms-products/products';
import { getLeadAllProductsMappingData } from './get-lead-referral-all-products-mapping-data';
import { getFormDataPropsHelper } from '~/src/helpers/get-form-data-props.helper';
import { onSendButtonsClickAnalytics } from './referral-all-products-layout.analytics';
import {
    headerBannerTitle,
    personalDataProcessingText,
} from '~/src/layouts/referral-all-products-layout/referral-all-products-layout.constants';
import { ATTR_NAMES_LEAD } from '~/src/models/constants/attr-names-lead.constants';
import { ATTR_NAMES_REFERRAL } from '~/src/models/constants/attr-names-referral.constants';
import { ADDRESS_WITHOUT_FIAS_ID_POPUP_ERROR } from '~/src/repositories/lead/constants';
import { WebAnalyticsEventCategoryEnum } from '~/src/models/enums/web-analytics.enum';
import {
    ClientsRequestLayoutProps,
    FormEvent,
} from '~/src/layouts/clients-request-layout/clients-request-layout.types';
import type { TypeFilesEnum } from '~/src/models/enums/type-files.enum';
import type { ChipsDataInterface } from '~/src/models/types/chips.types';
import type { QueryDataAllProducts } from '~/src/models/types/query-data-all-products';
import type { RootState } from '~/src/redux/store';
import * as Styled from './referral-all-products-layout.styles';

// TODO

interface ReferralAllProductsLayoutProps extends ClientsRequestLayoutProps {
    queryData: QueryDataAllProducts;
}

const ReferralAllProductsLayout: FC<ReferralAllProductsLayoutProps> = ({ dataProducts, regionsData, queryData }) => {
    const countCheckedProducts = useSelector(selectCountCheckedProducts);
    const referralCode = useSelector(selectReferralCode);
    const { opf } = useSelector(selectSuggestedOrganization);
    const getFilesData = useSelector(
        (state: RootState) => (typeField: TypeFilesEnum) => selectFilesData(state, typeField),
    );
    const isRequiredAddressField = useSelector(selectIsRequiredAddressField);

    const [chipsData, setChipsData] = useState<ChipsDataInterface>({ show: false, error: '' });
    const [needResetAdditionalBlock, setNeedResetAdditionalBlock] = useState(false);
    const [isInternetSelected, setIsInternetSelected] = useState(false);
    const [hasBackendErrorsInPopup, setHasBackendErrorsInPopup] = useState(false);

    const createLeadFormRef = useRef<HTMLFormElement>(null);
    const { needReset, triggerReset, onResetComplete } = useFormReset();
    const { state, resetForm, setFieldError } = useClientRequestForm();
    const showBadge = useBadgeHook(state.currentVariant);
    const { resetPopup, showPopup } = usePopupContent();

    const { createLeadAllReferral, isLoading } = useFetchCreateLeadAllReferral(setChipsData, () => {
        resetForm();
        dispatch(setClearCountCheckedProducts());
        createLeadFormRef.current?.reset();
        setNeedResetAdditionalBlock(true);

        setIsInternetSelected(false);
        setHasAddressError(false);
        triggerReset();
    },
    setHasBackendErrorsInPopup,
    (fieldError) => setFieldError(fieldError.field, fieldError.message));
    const hasMtsInternetProduct = determineProductAvailability(dataProducts, 'MTS_INTERNET');
    const productsListErrorRef = useRef(null);
    const phoneErrorRef = useRef(null);
    const dropDownOrganizationErrorRef = useRef(null);
    const innErrorRef = useRef(null);
    const regionErrorRef = useRef(null);
    const ogrnErrorRef = useRef(null);
    const emailErrorRef = useRef(null);
    const addressErrorRef = useRef(null);
    const selfEmployedFioErrorRef = useRef(null);
    const houseFiasIdRef = useRef<string>('');

    const scrollToRef = useScroll();
    const dispatch = useDispatch();
    const popupStack = useSelector(selectPopupsStack);
    const {
        hasAddressError,
        setHasAddressError,
        setHasOrganizationFieldError,
        shouldValidateOrganizationField,
    } = useLeadFormValidation();

    const closePopup = () => {
        const shouldResetPopup = hasBackendErrorsInPopup || hasInvalidRequiredAddress();
        !shouldResetPopup && resetPopup();
    };

    const { error: errorKey } = ATTR_NAMES_LEAD;

    useEffect(() => {
        dispatch(setIsShowGeolocationBlock(false));
        dispatch(setReferralCode(queryData?.agentId || queryData?.code));
    }, []);

    const checkErrors = (formData: FormData) => {
        return Object.values(Object.fromEntries(formData)).includes(errorKey);
    };

    const hasInvalidRequiredAddress = () => isRequiredAddressField && (!houseFiasIdRef.current || hasAddressError);

    const isRequiredAddressWithoutFias = () => isRequiredAddressField && !houseFiasIdRef.current;

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (hasBackendErrorsInPopup) {
            return;
        }

        if (isRequiredAddressWithoutFias()) {
            showPopup(
                <PopupContent
                    {...ADDRESS_WITHOUT_FIAS_ID_POPUP_ERROR}
                    subText={<Styled.WrapperPopup>{ADDRESS_WITHOUT_FIAS_ID_POPUP_ERROR.subText}</Styled.WrapperPopup>}
                    resetPopup={resetPopup}
                />,
            );
            return;
        }

        const formData = new FormData(event.target);
        const hasError = checkErrors(formData);
        const clientType = setClientType(state.currentVariant, opf) || '';

        const { domainUrl } = getFormDataPropsHelper(formData);

        if (domainUrl && typeof domainUrl === 'string' && domainUrl?.length) {
            return;
        }

        if (hasError || isLoading) {
            return;
        }

        const data = getLeadAllProductsMappingData({
            formData,
            getFilesData,
            regionsData,
            referralCode,
            clientType,
            houseFiasId: houseFiasIdRef.current,
        });

        if (Object.keys(data)?.length) {
            return await createLeadAllReferral(data);
        }
    };

    const onButtonClick = (event: SyntheticEvent<HTMLElement>) => {
        onSendButtonsClickAnalytics('otpravit_zayavku');
        const currentError =
            dropDownOrganizationErrorRef?.current ||
            phoneErrorRef?.current ||
            regionErrorRef?.current ||
            addressErrorRef?.current ||
            emailErrorRef?.current ||
            productsListErrorRef?.current;

        currentError && event.preventDefault();
        scrollToRef(currentError);
    };

    const onShowAdditionalBlock = () => {
        setNeedResetAdditionalBlock(false);
    };

    const handleCheckedProductsChange = useCallback((codes: string[]) => {
        setIsInternetSelected(codes.includes('MTS_INTERNET'));
    }, []);

    const handleChangeHouseFiasIdRef = (houseFiasId: string) => {
        houseFiasIdRef.current = houseFiasId;
    };

    return (
        <>
            <ReferralProductHeader regionsData={regionsData} closePopup={closePopup} />
            <Styled.PageContainerWrap>
                <Styled.HeadBanner>
                    <Styled.HeaderBannerTitle>{headerBannerTitle}</Styled.HeaderBannerTitle>
                    <Styled.HeaderBannerLogoContainer>
                        <Styled.HeaderBannerLogo src={BannerLogo} alt="Лого" quality={90} width={528} height={129} />
                    </Styled.HeaderBannerLogoContainer>
                </Styled.HeadBanner>
                <Styled.FormContainer>
                    <Styled.Form
                        ref={createLeadFormRef}
                        onSubmit={handleSubmit}
                        checkedOneProduct={!!countCheckedProducts}
                        hasAddressError={hasAddressError}
                        hasOrganizationFieldError={shouldValidateOrganizationField(state.currentVariant, opf)}
                    >
                        <Styled.DomainUrlFieldWrap name={ATTR_NAMES_REFERRAL.domainUrl} />
                        <ProductsBlock
                            dataProducts={dataProducts}
                            productsListErrorRef={productsListErrorRef}
                            needReset={needReset}
                            onResetComplete={onResetComplete}
                            onCheckedProductsChange={handleCheckedProductsChange}
                        />
                        <OrganizationInfoRef
                            dropDownOrganizationErrorRef={dropDownOrganizationErrorRef}
                            ogrnErrorRef={ogrnErrorRef}
                            innErrorRef={innErrorRef}
                            regionErrorRef={regionErrorRef}
                            regionsData={regionsData}
                            addressErrorRef={addressErrorRef}
                            inputErrorRef={phoneErrorRef}
                            emailErrorRef={emailErrorRef}
                            selfEmployedFioErrorRef={selfEmployedFioErrorRef}
                            organizationIsRequired
                            withAutoSelectRegion
                            eventCategory={WebAnalyticsEventCategoryEnum.createNewLeadRef}
                            hasMtsInternetProduct={hasMtsInternetProduct}
                            isInternetSelected={isInternetSelected}
                            onAddressValidationChange={setHasAddressError}
                            onOrganizationFieldValidationChange={setHasOrganizationFieldError}
                            onHouseFiasIdChange={handleChangeHouseFiasIdRef}
                        />
                        <AdditionallyBlock
                            hasNeedReset={needResetAdditionalBlock}
                            onShow={onShowAdditionalBlock}
                            eventCategory={WebAnalyticsEventCategoryEnum.createNewLeadRef}
                        />
                        <Styled.ButtonContainer>
                            <Styled.SubmitButton isLoading={isLoading} onClick={onButtonClick}>
                                Отправить заявку
                            </Styled.SubmitButton>
                            <Styled.PersonalDataProcessingText>
                                {personalDataProcessingText}
                            </Styled.PersonalDataProcessingText>
                        </Styled.ButtonContainer>
                    </Styled.Form>
                </Styled.FormContainer>
                {chipsData.show && (
                    <ChipsNotification
                        chipsText={chipsData.error as string}
                        onChipsDisappear={() => setChipsData((prev) => ({ ...prev, show: false }))}
                        top={110}
                    />
                )}
                <Popup popupStack={popupStack} closePopup={closePopup} />
                {showBadge && (
                    <InfoBadge icon={<SvgShapeIcon />}>
                        <Styled.BadgeText>
                            Для самозанятых доступны следующие продукты:
                            <Styled.BadgeTitleProduct>{' Интернет (ШПД)'}</Styled.BadgeTitleProduct>
                        </Styled.BadgeText>
                    </InfoBadge>
                )}
            </Styled.PageContainerWrap>
            <ReferralProductFooter />
        </>
    );
};

export default ReferralAllProductsLayout;
